// src/hooks/useReservations.js
// Hook para escuchar en tiempo real los horarios ocupados de un profesional:
// citas confirmadas (public_slots) + holds vigentes del checkout (slot_locks).

import { useState, useEffect, useMemo } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getDb } from '../services/firebase';
import { toast } from 'react-toastify';
import { mergeHoldsIntoReservas, localDateISO, hasActiveHoldWithinTolerance } from '../utils/slotHolds';

// Los holds vencen solos; onSnapshot no se dispara por el mero paso del tiempo,
// así que recalculamos periódicamente para liberar el horario en pantalla.
const HOLD_TICK_MS = 30_000;

export function useReservations(professionalId) {
  const [occupied, setOccupied] = useState({});
  const [lockDocs, setLockDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!professionalId) {
        setLoading(false);
        return;
    }

    const publicSlotsQuery = collection(getDb(), 'profiles', professionalId, 'public_slots');

    const unsubscribe = onSnapshot(
      publicSlotsQuery,
      (snapshot) => {
        const occupiedSlots = {};
        snapshot.forEach((d) => {
          const slot = d.data();
          occupiedSlots[slot.date] = occupiedSlots[slot.date] || {};
          occupiedSlots[slot.date][slot.time] = true;
        });
        setOccupied(occupiedSlots);
        setLoading(false);
      },
      (error) => {
        console.error('Error al escuchar slots públicos:', error);
        toast.error('No se pudo cargar la disponibilidad en tiempo real.');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [professionalId]);

  // Holds del checkout. Solo días de hoy en adelante, para no leer el histórico.
  useEffect(() => {
    if (!professionalId) {
      setLockDocs([]);
      return;
    }

    const todayISO = localDateISO();
    const locksQuery = query(
      collection(getDb(), 'profiles', professionalId, 'slot_locks'),
      where('date', '>=', todayISO)
    );

    const unsubscribe = onSnapshot(
      locksQuery,
      (snapshot) => {
        setLockDocs(snapshot.docs.map((d) => d.data()));
      },
      (error) => {
        // Fallo blando: sin holds el widget sigue funcionando y el backend
        // rechaza con 409 si el horario está tomado.
        console.error('Error al escuchar holds de horario:', error);
        setLockDocs([]);
      }
    );

    return () => unsubscribe();
  }, [professionalId]);

  // Recalcular mientras haya al menos un hold vigente (dentro de la misma
  // tolerancia que usa mergeHoldsIntoReservas para "verlo" ocupado). Antes se
  // chequeaba solo la PRESENCIA de la clave `holds`, que sigue existiendo
  // horas despues de vencido (el barrido nocturno es lo unico que la borra) -
  // eso hacia correr el timer sin parar en cualquier pagina del widget
  // publico que hubiera tenido un solo hold alguna vez ese dia.
  useEffect(() => {
    if (!hasActiveHoldWithinTolerance(lockDocs, Date.now())) return;

    const id = setInterval(() => {
      // Se re-evalua en cada tick (no solo al montar el efecto) porque el
      // vencimiento es cuestion de tiempo, no de un nuevo snapshot: sin este
      // chequeo interno el intervalo seguiria disparando para siempre una vez
      // arrancado, aunque todos los holds ya hubieran vencido.
      if (!hasActiveHoldWithinTolerance(lockDocs, Date.now())) {
        clearInterval(id);
        return;
      }
      setTick((t) => t + 1);
    }, HOLD_TICK_MS);

    return () => clearInterval(id);
  }, [lockDocs]);

  const reservas = useMemo(
    () => mergeHoldsIntoReservas(occupied, lockDocs, Date.now()),
    // `tick` fuerza el recálculo periódico; su valor no se usa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [occupied, lockDocs, tick]
  );

  return { reservas, loading };
}
